import { QuizGenerationError, QuizStudioAPI, toQuizLang } from './QuizStudioAPI';

/**
 * Pont « création IA » entre le jeu (iframe, régie TV UnQuiz) et la console,
 * partagé par les lanceurs v0.7.1 et v0.4.0 :
 * - `aiCapabilities` → `onAiCapabilities` : le jeu n'affiche la création IA que
 *   si la console sait la faire (pas de bouton qui tourne dans le vide sur une
 *   console plus ancienne ou dans le game-runner) ;
 * - `generateQuiz` → `onQuizGenerateProgress`* puis `onQuizGenerated` /
 *   `onQuizGenerateError`, corrélés par `requestId` ;
 * - `cancelGenerateQuiz` : coupe la génération en cours (sinon facturée jusqu'au bout).
 */
const inFlight = new Map<string, AbortController>();

type Post = (message: { action: string; data: unknown }) => void;

/** @returns true si le message relevait du pont IA (traité ici). */
export function handleAiBridgeMessage(
    data: { action?: string; data?: any },
    ctx: { fromGame: boolean; clientID?: string; post: Post }
): boolean {
    const { action } = data;
    if (action !== 'aiCapabilities' && action !== 'generateQuiz' && action !== 'cancelGenerateQuiz') return false;
    // Uniquement depuis l'iframe du jeu piloté : une autre frame ou une extension
    // ne doit pas pouvoir déclencher des générations facturées.
    if (!ctx.fromGame) return true;

    if (action === 'aiCapabilities') {
        // Sans établissement, le quiz ne pourrait pas être enregistré.
        ctx.post({ action: 'onAiCapabilities', data: { generateQuiz: !!ctx.clientID } });
        return true;
    }

    const { requestId, prompt, count, difficulty, lang, source } = data.data ?? {};
    if (typeof requestId !== 'string') return true;

    if (action === 'cancelGenerateQuiz') {
        inFlight.get(requestId)?.abort();
        inFlight.delete(requestId);
        return true;
    }

    if (!ctx.clientID) {
        ctx.post({ action: 'onQuizGenerateError', data: { requestId, code: 'GENERATION_FAILED' } });
        return true;
    }
    const controller = new AbortController();
    inFlight.set(requestId, controller);
    QuizStudioAPI.generateQuiz(
        {
            theme: String(prompt ?? ''),
            count,
            difficulty,
            lang: toQuizLang(lang),
            source: typeof source === 'string' ? source : undefined,
            clientID: ctx.clientID,
        },
        (done, total) => ctx.post({ action: 'onQuizGenerateProgress', data: { requestId, done, total } }),
        controller.signal
    )
        .then((quiz) => ctx.post({ action: 'onQuizGenerated', data: { requestId, quiz } }))
        .catch((error: unknown) => {
            if (controller.signal.aborted) return; // annulé par le jeu : il n'attend plus rien
            const known = error instanceof QuizGenerationError;
            ctx.post({
                action: 'onQuizGenerateError',
                data: { requestId, code: known ? error.code : 'GENERATION_FAILED', detail: known ? error.detail : undefined },
            });
        })
        .finally(() => inFlight.delete(requestId));
    return true;
}
